/**
 * One frame of a gesture, applied to a body somebody else built.
 *
 * Four of them: turn a man to face Kimmo, put his arm out at a point in the
 * world, put both arms up over his head, and wave. They are given the object
 * to turn and the point to turn it towards, and they read nothing back - so a
 * pose can be re-applied every frame without the figure accumulating drift.
 *
 * Rendering only. Nothing in this file may ever be imported by `src/sim`.
 */

import * as THREE from 'three';
import { type GreetingPose } from './Greeting';
import {
  CHEER_ELBOW,
  CHEER_LIFT,
  CHEER_SEED,
  CHEER_SPLAY,
  CHEER_STAGGER,
  GREETING_SIDE,
  POINTING_ELBOW,
} from './People';
import { angleDelta, type Vec3 } from '../utils/math';
import { mulberry32 } from '../utils/prng';

/**
 * One frame of it, on the arm groups `standerFigure` built.
 *
 * The same 'YXZ' order and the same two turns `raiseArms` uses, and for the
 * same reason: the splay has to happen before the lift. Rolled the other way
 * round the arm goes up first and then the roll swings the raised arm, which
 * brings his own hand across his own face.
 */
export function poseGreetingArm(
  arm: THREE.Object3D,
  elbow: THREE.Object3D | null,
  pose: GreetingPose,
): void {
  arm.rotation.set(-(Math.PI / 2 + pose.lift), 0, GREETING_SIDE * pose.splay, 'YXZ');
  if (elbow) elbow.rotation.x = pose.elbow;
}

/**
 * Lift one arm and aim it at a point in the world.
 *
 * Both angles are read off the target rather than chosen. The yaw has to come
 * back out of the body first - the arm is a child of a group already turned
 * by `rotationY`, so the difference between the two is how far off his own
 * shoulders he is pointing - and the lift is then the slope from his shoulder
 * to the thing itself.
 *
 * That second one is the whole reason the target carries a height. An arm
 * held at some flattering angle above the horizontal is not pointing at
 * anything; it is a salute, and it reads as one. A machine on a desk is
 * *below* a standing man's shoulder, so the arm that points at it comes down
 * a little - and an arm coming down at something is unmistakably an arm
 * showing you where to go.
 *
 * The arm groups hang straight down, so the lift is a rotation about X
 * through a right angle plus that slope. 'YXZ' rather than the default,
 * because the two angles have to happen in that order - swing the whole arm
 * round, then lift it. The other way lifts it and then swings the lift, and
 * the hand lands somewhere nobody's shoulder goes.
 */
export function pointArm(
  figure: THREE.Object3D,
  at: [number, number, number],
  elbowBend = POINTING_ELBOW,
): void {
  const dx = at[0] - figure.position.x;
  const dz = at[2] - figure.position.z;
  // Wrapped, because the sign of this is what picks the hand below. A man
  // looking one way and pointing well round behind himself - which is what
  // Mäysä does once he is out of the doorway - comes out of the raw
  // subtraction as four negative radians, and four negative radians is the
  // same arm in the same place with the wrong shoulder under it.
  const yaw = angleDelta(figure.rotation.y, Math.atan2(dx, dz));

  // Tracking can move the target across the chest and switch pointing hands.
  for (const name of ['kasi-vasen', 'kasi-oikea']) {
    const arm = figure.getObjectByName(name);
    if (!arm) continue;
    arm.rotation.set(0, 0, 0);
    const elbow = arm.getObjectByName('kyynarpaa');
    if (elbow) elbow.rotation.x = 0;
  }

  // The arm on the side the point is on, so a raised one never has to cross
  // the chest to get there. A man square to what he is pointing at could use
  // either; he gets the one that leaves his own face and his own cap clear of
  // his sleeve, which is the arm that swings away from the way in.
  const arm = figure.getObjectByName(yaw > 0 ? 'kasi-vasen' : 'kasi-oikea');
  if (!arm) return;

  // The figure stands on the ground at y = 0, so the shoulder group's own
  // height is also its height in the world.
  const slope = Math.atan2(at[1] - arm.position.y, Math.hypot(dx, dz));
  arm.rotation.set(-(Math.PI / 2 + slope), yaw, 0, 'YXZ');

  // And the elbow just short of straight. A locked-out arm is a salute
  // whatever angle it is at; a little bend left in it is a man's arm.
  const elbow = arm.getObjectByName('kyynarpaa');
  if (elbow) elbow.rotation.x = elbowBend;
}

/**
 * Both arms up, which is the only thing anybody standing in front of a rig
 * at nine in the morning is doing with them. See `StanderSpec.cheering`.
 *
 * The opposite of `pointArm` and built the opposite way round on purpose:
 * that one reads both of its angles off a thing in the world, because an arm
 * aimed at something is aimed at something. There is nothing over a crowd to
 * aim at, so these angles are chosen, and they are chosen once - `CHEER_LIFT`
 * and the two beside it.
 *
 * An arm group hangs straight down, so putting one up is two turns and the
 * order they happen in is what makes them mean anything: the roll takes the
 * hand out to the side, and then the lift swings it up and over. 'YXZ'
 * applies them in that order. The other way round lifts the arm first and
 * then rolls the lift, which brings the hand across his own face.
 *
 * The lift is varied per arm rather than per man, so that nobody in the
 * crowd has both hands at the same height and no two hands in it are level.
 * That is the entire difference between a crowd and a chorus line, and it
 * costs one roll of a seeded stream.
 *
 * What comes back is the arms it raised and the angle it raised each one to,
 * because this pose is also the *top* of the one `renderCrowd` swings them
 * through - see `armDrop`. The blackout lowers both arms instead.
 */
export function raiseArms(
  figure: THREE.Object3D,
  index: number,
): { arm: THREE.Object3D; lift: number }[] {
  const rng = mulberry32(0x51eeb0 + (CHEER_SEED + index) * 977);
  const raised: { arm: THREE.Object3D; lift: number }[] = [];
  for (const [side, name] of [
    [-1, 'kasi-oikea'],
    [1, 'kasi-vasen'],
  ] as [number, string][]) {
    const arm = figure.getObjectByName(name);
    if (!arm) continue;
    const lift = CHEER_LIFT + (rng() - 0.5) * CHEER_STAGGER;
    arm.rotation.set(-(Math.PI / 2 + lift), 0, side * CHEER_SPLAY, 'YXZ');
    const elbow = arm.getObjectByName('kyynarpaa');
    if (elbow) elbow.rotation.x = CHEER_ELBOW;
    raised.push({ arm, lift });
  }
  return raised;
}

export function faceKimmo(figure: THREE.Object3D, head: THREE.Object3D | undefined, eye: Readonly<Vec3>): void {
  const dx = eye.x - figure.position.x;
  const dz = eye.z - figure.position.z;
  if (dx !== 0 || dz !== 0) figure.rotation.y = Math.atan2(dx, dz);
  if (head) {
    head.rotation.set(-Math.atan2(
      eye.y - figure.position.y - head.position.y, Math.hypot(dx, dz),
    ), 0, 0);
  }
}
